import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ThumbUpOffAltOutlinedIcon from "@mui/icons-material/ThumbUpOffAltOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";
import { getUserById } from "../../api/User/userApi";
import { getCustomers } from "../../api/Customer/customerApi";
import { getMarcas } from "../../api/Marcas/marcaApi";
import {
  acquireSkillFichaLock,
  createSkillFicha,
  getSkillFichaById,
  heartbeatSkillFichaLock,
  markSkillFichaDesignComplete,
  markSkillFichaReady,
  releaseSkillFichaLock,
  reopenSkillFichaDesign,
  updateSkillFicha,
} from "../../api/Skills/skillFichasApi";
import { publishSkillVersion } from "../../api/Skills/skillVersionsApi";
import {
  buildEmptyForm,
  hydrateForm,
  slugifySkill,
  toPayload,
} from "./skillFichaFormDefaults";
import { isSkillsApprover } from "./skillFichaHelpers";
import SkillFichaHelpPanel from "./SkillFichaHelpPanel";
import SkillFichaWorkflowBar from "./SkillFichaWorkflowBar";
import SkillConfirmDialog from "./SkillConfirmDialog";

const SECTIONS = [
  {
    id: "cabecera",
    titleKey: "skillFicha.sec.basic.title",
    subtitleKey: "skillFicha.sec.basic.sub",
    icon: AutoAwesomeOutlinedIcon,
    defaultOpen: true,
    accent: "#00897b",
  },
  {
    id: "identidad",
    titleKey: "skillFicha.sec.identity.title",
    subtitleKey: "skillFicha.sec.identity.sub",
    icon: PersonOutlineOutlinedIcon,
    defaultOpen: true,
    accent: "#1565c0",
  },
  {
    id: "visual",
    titleKey: "skillFicha.sec.visual.title",
    subtitleKey: "skillFicha.sec.visual.sub",
    icon: PaletteOutlinedIcon,
    accent: "#6a1b9a",
  },
  {
    id: "producto",
    titleKey: "skillFicha.sec.product.title",
    subtitleKey: "skillFicha.sec.product.sub",
    icon: StyleOutlinedIcon,
    defaultOpen: true,
    accent: "#ef6c00",
  },
  {
    id: "memoria",
    titleKey: "skillFicha.sec.memory.title",
    subtitleKey: "skillFicha.sec.memory.sub",
    icon: HistoryOutlinedIcon,
    accent: "#455a64",
  },
  {
    id: "agente",
    titleKey: "skillFicha.sec.agent.title",
    subtitleKey: "skillFicha.sec.agent.sub",
    icon: HandshakeOutlinedIcon,
    accent: "#00838f",
  },
  {
    id: "buenos",
    titleKey: "skillFicha.sec.examples.title",
    subtitleKey: "skillFicha.sec.examples.sub",
    icon: ThumbUpOffAltOutlinedIcon,
    defaultOpen: true,
    accent: "#c62828",
  },
  {
    id: "checklist",
    titleKey: "skillFicha.sec.checklist.title",
    subtitleKey: "skillFicha.sec.checklist.sub",
    icon: FactCheckOutlinedIcon,
    accent: "#2e7d32",
  },
  {
    id: "cierre",
    titleKey: "skillFicha.sec.notes.title",
    subtitleKey: "skillFicha.sec.notes.sub",
    icon: NotesOutlinedIcon,
    accent: "#5d4037",
  },
];

function Section({ section, children, readOnly, index }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const Icon = section.icon;
  const accent = section.accent || theme.palette.primary.main;
  return (
    <Accordion
      defaultExpanded={!!section.defaultOpen}
      disableGutters
      sx={{
        mb: 2.25,
        borderRadius: "16px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
        border: "1px solid",
        borderColor: alpha(accent, 0.16),
        boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.035)}`,
        bgcolor: theme.palette.background.paper,
        transition: "border-color 0.2s, box-shadow 0.2s",
        "&.Mui-expanded": {
          borderColor: alpha(accent, 0.32),
          boxShadow: `0 10px 28px ${alpha(accent, 0.08)}`,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: { xs: 2, md: 2.5 },
          minHeight: 72,
          background: `linear-gradient(90deg, ${alpha(accent, 0.09)} 0%, ${alpha(accent, 0.02)} 52%, transparent 100%)`,
          borderLeft: `4px solid ${accent}`,
          "& .MuiAccordionSummary-content": { my: 1.35 },
        }}
      >
        <Stack direction="row" spacing={1.75} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.25,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(accent, 0.12),
              color: accent,
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                fontWeight={800}
                sx={{ letterSpacing: "-0.015em", fontSize: "1.05rem" }}
              >
                {t(section.titleKey)}
              </Typography>
              {typeof index === "number" && (
                <Chip
                  size="small"
                  label={`${index + 1}/${SECTIONS.length}`}
                  sx={{
                    height: 22,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: alpha(accent, 0.12),
                    color: accent,
                  }}
                />
              )}
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.15, lineHeight: 1.35 }}
            >
              {t(section.subtitleKey)}
              {readOnly ? t("skillFicha.readOnlySuffix") : ""}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: { xs: 2, md: 2.75 },
          pt: 2,
          pb: { xs: 2.5, md: 3 },
          bgcolor: alpha(theme.palette.grey[50], 0.55),
          borderTop: "1px solid",
          borderColor: alpha(accent, 0.1),
        }}
      >
        <Grid container spacing={{ xs: 2, md: 2.5 }}>
          {children}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * Form field with readable spacing. Long values auto-expand to multiline
 * so content is never clipped in a single-line box.
 */
function Field({
  xs = 12,
  md = 6,
  readOnly,
  multiline,
  minRows,
  maxRows = 14,
  helperText,
  sx,
  ...props
}) {
  const theme = useTheme();
  const value = props.value;
  const valueLen = String(value ?? "").length;
  const hasNewlines = /\n/.test(String(value ?? ""));
  const autoMulti = Boolean(multiline) || hasNewlines || valueLen > 72;
  const computedRows =
    minRows ??
    (autoMulti
      ? Math.min(10, Math.max(2, Math.ceil(valueLen / 55) || 2))
      : undefined);

  return (
    <Grid item xs={xs} md={md}>
      <TextField
        fullWidth
        size="medium"
        multiline={autoMulti}
        minRows={autoMulti ? computedRows : undefined}
        maxRows={autoMulti ? maxRows : undefined}
        helperText={helperText}
        FormHelperTextProps={{
          sx: {
            mx: 0.25,
            mt: 0.85,
            mb: 0.25,
            lineHeight: 1.4,
            fontSize: "0.78rem",
            color: "text.secondary",
          },
        }}
        sx={{
          ...pageFieldSx(theme),
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            alignItems: autoMulti ? "flex-start" : "center",
            fontSize: "0.9375rem",
            lineHeight: 1.5,
            color: theme.palette.text.primary,
            "& textarea": {
              overflow: "auto !important",
              resize: "vertical",
            },
          },
          "& .MuiInputLabel-root": {
            fontWeight: 600,
          },
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
            WebkitTextFillColor: readOnly
              ? theme.palette.text.primary
              : undefined,
            opacity: readOnly ? 0.92 : 1,
          },
          "& .Mui-disabled .MuiInputBase-input": {
            WebkitTextFillColor: theme.palette.text.primary,
            opacity: 0.85,
          },
          ...sx,
        }}
        disabled={readOnly}
        {...props}
      />
    </Grid>
  );
}

export default function SkillFichaFormPage() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === "new";
  const { contentMargin, isMobile } = useContext(SidebarContext);

  const [form, setForm] = useState(buildEmptyForm);
  const [meta, setMeta] = useState({});
  const [customers, setCustomers] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [sessionUser, setSessionUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [helpOpen, setHelpOpen] = useState(true);
  const [lockedByOther, setLockedByOther] = useState(null);
  const [confirmKind, setConfirmKind] = useState(null);
  const lockOwnedRef = useRef(false);

  const isMiguel = useMemo(() => isSkillsApprover(sessionUser), [sessionUser]);

  const designFinished =
    !!meta.DesignCompletedAt || form.Status === "design_done";
  const isFinalized =
    form.Status === "ready" ||
    form.Status === "skill_created" ||
    form.Status === "published";

  const readOnly =
    !!lockedByOther || isFinalized || (designFinished && !isMiguel);

  const canEdit = !readOnly && !isNew;
  const canDesignComplete =
    !isNew && canEdit && !designFinished && !lockedByOther;
  const canMarkReady =
    isMiguel &&
    designFinished &&
    form.Status !== "ready" &&
    form.Status !== "skill_created" &&
    form.Status !== "published";
  const canPublish =
    isMiguel && (form.Status === "ready" || form.Status === "skill_created");
  const canReopenPublished =
    isMiguel &&
    (form.Status === "published" ||
      form.Status === "ready" ||
      form.Status === "skill_created");
  const isPublished = form.Status === "published";

  const selectedCustomer = useMemo(
    () =>
      customers.find((c) => String(c.id) === String(form.CustomerId)) || null,
    [customers, form.CustomerId],
  );
  const selectedMarca = useMemo(
    () =>
      marcas.find((m) => String(m.ID_MARCA || m.id) === String(form.MarcaId)) ||
      null,
    [marcas, form.MarcaId],
  );

  const applyRow = useCallback((row) => {
    setForm(hydrateForm(row));
    setMeta(row || {});
  }, []);

  const loadFicha = useCallback(async () => {
    if (isNew) return;
    const row = await getSkillFichaById(id);
    applyRow(row);
    return row;
  }, [applyRow, id, isNew]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }
        const decoded = jwtDecode(token);
        const user = await getUserById(decoded.userId);
        if (!user?.Permiso_Skills) {
          if (!cancelled) setForbidden(true);
          return;
        }
        if (!cancelled) setSessionUser(user);

        const [custs, marks] = await Promise.all([
          getCustomers().catch(() => []),
          getMarcas().catch(() => []),
        ]);
        if (cancelled) return;
        setCustomers(Array.isArray(custs) ? custs : []);
        setMarcas(Array.isArray(marks) ? marks : []);
        await loadFicha();
      } catch (err) {
        console.error(err);
        if (err?.response?.status === 403) setForbidden(true);
        else if (!cancelled) setError(t("skillFicha.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadFicha, navigate, isNew, t]);

  useEffect(() => {
    if (isNew || !id) return undefined;

    let heartbeatId;
    let cancelled = false;

    (async () => {
      try {
        const res = await acquireSkillFichaLock(id);
        if (cancelled) return;
        lockOwnedRef.current = true;
        setLockedByOther(null);
        if (res?.lockOwnerName) setMeta((m) => ({ ...m, ...res }));
        heartbeatId = window.setInterval(() => {
          heartbeatSkillFichaLock(id).catch(() => {});
        }, 60000);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 423) {
          const name =
            err.response?.data?.lockOwnerName || t("skillFicha.someoneElse");
          setLockedByOther(name);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (heartbeatId) window.clearInterval(heartbeatId);
      if (lockOwnedRef.current) {
        releaseSkillFichaLock(id).catch(() => {});
        lockOwnedRef.current = false;
      }
    };
  }, [id, isNew, t]);

  const setRoot = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setSection = (sectionKey, field, value) => {
    setForm((prev) => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], [field]: value },
    }));
  };

  const suggestSlug = () => {
    const base =
      form.SkillSlug ||
      [selectedCustomer?.name || form.Titulo, form.CategoriaFoco]
        .filter(Boolean)
        .join("-");
    setRoot("SkillSlug", slugifySkill(base));
  };

  const savePayload = useCallback(async () => {
    const payload = toPayload({
      ...form,
      SkillSlug:
        form.SkillSlug ||
        slugifySkill(
          [form.Titulo || selectedCustomer?.name, form.CategoriaFoco]
            .filter(Boolean)
            .join("-"),
        ),
    });
    if (!payload.Titulo?.trim()) {
      throw new Error(t("skillFicha.needTitle"));
    }
    if (isNew) {
      const created = await createSkillFicha(payload);
      navigate(`/skills/${created.id}`, { replace: true });
      return created;
    }
    return updateSkillFicha(id, payload);
  }, [form, id, isNew, navigate, selectedCustomer?.name, t]);

  const handleSaveDraft = async () => {
    setSaving(true);
    setError("");
    setOkMsg("");
    try {
      const updated = await savePayload();
      if (!isNew) applyRow(updated);
      setOkMsg(isNew ? t("skillFicha.created") : t("skillFicha.saved"));
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || err?.message;
      if (err?.response?.status === 423 && msg) {
        setLockedByOther(
          err.response?.data?.lockOwnerName || t("skillFicha.someoneElse"),
        );
      }
      setError(msg || t("skillFicha.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDesignComplete = async () => {
    setConfirmKind(null);
    setSaving(true);
    setError("");
    setOkMsg("");
    try {
      await savePayload();
      const updated = await markSkillFichaDesignComplete(
        id,
        form.CierreJson?.comentario || "",
      );
      applyRow(updated);
      lockOwnedRef.current = false;
      setOkMsg(t("skillFicha.designDoneOk"));
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.error ||
          err?.message ||
          t("skillFicha.designDoneError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReady = async () => {
    setConfirmKind(null);
    setSaving(true);
    setError("");
    try {
      const updated = await markSkillFichaReady(id);
      applyRow(updated);
      setOkMsg(t("skillFicha.readyOk"));
    } catch (err) {
      setError(err?.response?.data?.error || t("skillFicha.readyError"));
    } finally {
      setSaving(false);
    }
  };

  const handlePublishSkill = async () => {
    setConfirmKind(null);
    setSaving(true);
    setError("");
    try {
      const res = await publishSkillVersion(Number(id));
      if (res?.fichaStatus) {
        setForm((prev) => ({ ...prev, Status: res.fichaStatus }));
      }
      setOkMsg(
        [
          t("skillFicha.publishedOk", { version: res?.version?.Version || "" }),
          res.driveNote
            ? t("skillFicha.driveNote", { note: res.driveNote })
            : "",
          res.repoPath ? t("skillFicha.repoPath", { path: res.repoPath }) : "",
        ]
          .filter(Boolean)
          .join(" "),
      );
    } catch (err) {
      setError(err?.response?.data?.error || t("skillFicha.publishError"));
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async () => {
    setConfirmKind(null);
    setSaving(true);
    try {
      const updated = await reopenSkillFichaDesign(id);
      applyRow(updated);
      setLockedByOther(null);
      setOkMsg(t("skillFicha.reopenOk"));
    } catch (err) {
      setError(err?.response?.data?.error || t("skillFicha.reopenError"));
    } finally {
      setSaving(false);
    }
  };

  const confirmDialogProps = {
    designComplete: {
      title: t("skillFicha.confirm.designTitle"),
      description: t("skillFicha.confirm.designBody"),
      confirmLabel: t("skillFicha.confirm.designOk"),
      cancelLabel: t("skillFicha.confirm.keepEditing"),
      eyebrow: t("skillFicha.confirm.designEyebrow"),
      tone: "secondary",
      confirmColor: "secondary",
      icon: CheckCircleOutlineIcon,
      onConfirm: handleDesignComplete,
    },
    markReady: {
      title: t("skillFicha.confirm.readyTitle"),
      description: t("skillFicha.confirm.readyBody"),
      confirmLabel: t("skillFicha.confirm.readyOk"),
      cancelLabel: t("skillFicha.confirm.cancel"),
      eyebrow: t("skillFicha.confirm.readyEyebrow"),
      tone: "primary",
      confirmColor: "primary",
      icon: VerifiedOutlinedIcon,
      onConfirm: handleMarkReady,
    },
    publish: {
      title: t("skillFicha.confirm.publishTitle"),
      description: t("skillFicha.confirm.publishBody"),
      confirmLabel: t("skillFicha.confirm.publishOk"),
      cancelLabel: t("skillFicha.confirm.cancel"),
      eyebrow: t("skillFicha.confirm.publishEyebrow"),
      tone: "info",
      confirmColor: "info",
      icon: RocketLaunchOutlinedIcon,
      onConfirm: handlePublishSkill,
    },
    reopen: {
      title: t("skillFicha.confirm.reopenTitle"),
      description: t("skillFicha.confirm.reopenBody"),
      confirmLabel: t("skillFicha.confirm.reopenOk"),
      cancelLabel: t("skillFicha.confirm.cancel"),
      eyebrow: t("skillFicha.confirm.reopenEyebrow"),
      tone: "warning",
      confirmColor: "warning",
      icon: ReplayOutlinedIcon,
      onConfirm: handleReopen,
    },
  }[confirmKind];

  if (forbidden) {
    return (
      <Box sx={{ ...contentMargin, p: 3 }}>
        <Alert severity="warning">{t("skillFicha.forbidden")}</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          ...contentMargin,
          display: "flex",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const ActionBar = (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      alignItems="center"
      flexWrap="wrap"
      sx={{
        "& .MuiButton-root": {
          height: 36,
          textTransform: "none",
          fontWeight: 700,
          px: 1.5,
          whiteSpace: "nowrap",
        },
        "& .MuiChip-root": {
          height: 36,
        },
      }}
    >
      <Button
        size="small"
        variant={helpOpen ? "contained" : "outlined"}
        startIcon={<MenuBookOutlinedIcon />}
        onClick={() => setHelpOpen((v) => !v)}
      >
        {helpOpen ? t("skillFicha.hideGuide") : t("skillFicha.showGuide")}
      </Button>

      {!readOnly && (
        <Button
          size="small"
          variant="outlined"
          disabled={saving}
          onClick={handleSaveDraft}
          startIcon={<SaveOutlinedIcon />}
        >
          {t("skillFicha.saveDraft")}
        </Button>
      )}
      {canDesignComplete && (
        <Button
          size="small"
          variant="contained"
          color="secondary"
          disabled={saving}
          onClick={() => setConfirmKind("designComplete")}
          startIcon={<CheckCircleOutlineIcon />}
        >
          {t("skillFicha.designDoneBtn")}
        </Button>
      )}
      {canMarkReady && (
        <Button
          size="small"
          variant="contained"
          disabled={saving}
          sx={pagePrimaryButtonSx}
          onClick={() => setConfirmKind("markReady")}
          startIcon={<VerifiedOutlinedIcon />}
        >
          {t("skillFicha.markReady")}
        </Button>
      )}

      {/* Publicado: primero reabrir. Listo/validado: publicar. */}
      {canReopenPublished && isPublished && (
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={saving}
          onClick={() => setConfirmKind("reopen")}
          startIcon={<ReplayOutlinedIcon />}
        >
          {t("skillFicha.reopenUpdate")}
        </Button>
      )}
      {canPublish && (
        <Button
          size="small"
          variant="contained"
          color="info"
          disabled={saving}
          onClick={() => setConfirmKind("publish")}
          startIcon={<RocketLaunchOutlinedIcon />}
        >
          {t("skillFicha.publish")}
        </Button>
      )}
      {canReopenPublished && !isPublished && (
        <Button
          size="small"
          variant="outlined"
          disabled={saving}
          onClick={() => setConfirmKind("reopen")}
          startIcon={<ReplayOutlinedIcon />}
        >
          {t("skillFicha.reopen")}
        </Button>
      )}
      {isMiguel && designFinished && !isFinalized && (
        <Button
          size="small"
          variant="outlined"
          disabled={saving}
          onClick={() => setConfirmKind("reopen")}
          startIcon={<ReplayOutlinedIcon />}
        >
          {t("skillFicha.reopenDesign")}
        </Button>
      )}
      {readOnly && (
        <Chip
          icon={<VisibilityOutlinedIcon />}
          label={t("skillFicha.readMode")}
          size="small"
          variant="outlined"
          sx={{ "& .MuiChip-label": { fontWeight: 600 } }}
        />
      )}
    </Stack>
  );

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        height: "100dvh",
        width: "100%",
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        backgroundImage: `radial-gradient(1200px 400px at 10% -10%, ${alpha(theme.palette.primary.main, 0.1)}, transparent), radial-gradient(900px 360px at 100% 0%, ${alpha("#6a1b9a", 0.06)}, transparent)`,
        ...contentMargin,
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.5, md: 2.5 },
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(10px)",
          zIndex: 3,
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", lg: "center" }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/skills")}
              size="small"
              sx={{ flexShrink: 0, height: 36 }}
            >
              {t("skillFicha.back")}
            </Button>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: { xs: "normal", md: "nowrap" },
                }}
              >
                {isNew
                  ? t("skillFicha.newTitle")
                  : form.Titulo || t("skillFicha.fallbackTitle")}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.5 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("skillFicha.subtitle")}
                </Typography>
                {isPublished && (
                  <Chip
                    size="small"
                    color="info"
                    icon={<RocketLaunchOutlinedIcon />}
                    label={t("skillFicha.published")}
                  />
                )}
                {form.Status === "ready" && (
                  <Chip
                    size="small"
                    color="success"
                    label={t("skillFicha.ready")}
                  />
                )}
                {designFinished &&
                  form.Status !== "ready" &&
                  form.Status !== "published" &&
                  form.Status !== "skill_created" && (
                    <Chip
                      size="small"
                      color="warning"
                      label={t("skillFicha.doneBy", {
                        name:
                          meta.DesignCompletedByUsername ||
                          t("skillFicha.design"),
                      })}
                    />
                  )}
                {form.Status === "draft" && !designFinished && (
                  <Chip size="small" label={t("skillFicha.inProgress")} />
                )}
                {readOnly && (
                  <Chip
                    icon={<VisibilityOutlinedIcon />}
                    label={t("skillFicha.readOnly")}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          </Stack>

          {!isMobile && ActionBar}
        </Stack>

        {!isNew && (
          <Box sx={{ mt: 1.5 }}>
            <SkillFichaWorkflowBar
              status={form.Status}
              designCompletedAt={meta.DesignCompletedAt}
              compact
            />
          </Box>
        )}
      </Box>

      {/* Alerts strip */}
      {(lockedByOther || (designFinished && !isMiguel) || error || okMsg) && (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 1.25, flexShrink: 0 }}>
          <Stack spacing={1}>
            {lockedByOther && (
              <Alert severity="warning" icon={<LockOutlinedIcon />}>
                <Trans
                  i18nKey="skillFicha.lockAlert"
                  values={{ name: lockedByOther }}
                  components={{ b: <b /> }}
                />
              </Alert>
            )}
            {designFinished && !isMiguel && (
              <Alert severity="info">{t("skillFicha.designLocked")}</Alert>
            )}
            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            {okMsg && (
              <Alert severity="success" onClose={() => setOkMsg("")}>
                {okMsg}
              </Alert>
            )}
          </Stack>
        </Box>
      )}

      {/* Two columns */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: helpOpen ? "minmax(320px, 0.95fr) minmax(0, 1.35fr)" : "1fr",
          },
          gap: { xs: 0, lg: 2 },
          px: { xs: 0, lg: 2.5 },
          py: { xs: 0, lg: 2 },
        }}
      >
        {/* Help column */}
        {helpOpen && (
          <Box
            sx={{
              minHeight: 0,
              height: { xs: "42vh", lg: "100%" },
              order: { xs: 1, lg: 0 },
              borderBottom: { xs: "1px solid", lg: "none" },
              borderColor: "divider",
            }}
          >
            <SkillFichaHelpPanel
              open={helpOpen}
              onClose={() => setHelpOpen(false)}
              isMiguel={isMiguel}
              variant="sidebar"
            />
          </Box>
        )}

        {/* Form column */}
        <Box
          sx={{
            minHeight: 0,
            height: "100%",
            overflow: "auto",
            order: { xs: 2, lg: 0 },
            px: { xs: 1.5, lg: 0.75 },
            pb: { xs: 12, lg: 4 },
            pt: { xs: 1.5, lg: 0.5 },
            maxWidth: 980,
            mx: "auto",
            width: "100%",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(theme.palette.primary.main, 0.25),
              borderRadius: 4,
            },
          }}
        >
          {!helpOpen && isMobile && (
            <Alert
              severity="info"
              sx={{ mb: 1.5 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setHelpOpen(true)}
                >
                  {t("skillFicha.openGuide")}
                </Button>
              }
            >
              {t("skillFicha.mobileGuide")}
            </Alert>
          )}

          <Section section={SECTIONS[0]} readOnly={readOnly} index={0}>
            <Field
              label={t("skillFicha.fields.title")}
              value={form.Titulo}
              onChange={(e) => setRoot("Titulo", e.target.value)}
              placeholder={t("skillFicha.fields.titlePh")}
              md={12}
              readOnly={readOnly}
            />
            <Grid item xs={12} md={6}>
              <Autocomplete
                disabled={readOnly}
                options={customers}
                getOptionLabel={(o) => o?.name || ""}
                value={selectedCustomer}
                onChange={(_, value) => setRoot("CustomerId", value?.id || "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("skillFicha.fields.client")}
                    size="medium"
                    sx={pageFieldSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                disabled={readOnly}
                options={marcas}
                getOptionLabel={(o) => o?.MARCA || o?.name || ""}
                value={selectedMarca}
                onChange={(_, value) =>
                  setRoot("MarcaId", value?.ID_MARCA || value?.id || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("skillFicha.fields.brand")}
                    size="medium"
                    sx={pageFieldSx}
                  />
                )}
              />
            </Grid>
            <Field
              label={t("skillFicha.fields.category")}
              value={form.CategoriaFoco}
              onChange={(e) => setRoot("CategoriaFoco", e.target.value)}
              readOnly={readOnly}
              multiline
              minRows={2}
              md={12}
            />
            <Field
              label={t("skillFicha.fields.season")}
              value={form.TemporadaRef}
              onChange={(e) => setRoot("TemporadaRef", e.target.value)}
              placeholder={t("skillFicha.fields.seasonPh")}
              readOnly={readOnly}
              md={6}
            />
            <Field
              label={t("skillFicha.fields.slug")}
              value={form.SkillSlug}
              onChange={(e) => setRoot("SkillSlug", e.target.value)}
              onBlur={suggestSlug}
              helperText={t("skillFicha.fields.slugHelp")}
              readOnly={readOnly}
              md={6}
            />
          </Section>

          <Section section={SECTIONS[1]} readOnly={readOnly} index={1}>
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.phrase")}
              value={form.IdentidadJson.frase}
              onChange={(e) =>
                setSection("IdentidadJson", "frase", e.target.value)
              }
              multiline
              minRows={3}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.phraseHelp")}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.target")}
              value={form.IdentidadJson.targetEdadMindset}
              onChange={(e) =>
                setSection("IdentidadJson", "targetEdadMindset", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.targetHelp")}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.occasion")}
              value={form.IdentidadJson.ocasionUso}
              onChange={(e) =>
                setSection("IdentidadJson", "ocasionUso", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
            <Field
              xs={12}
              md={6}
              label={t("skillFicha.fields.feel")}
              value={form.IdentidadJson.sensacionDeseada}
              onChange={(e) =>
                setSection("IdentidadJson", "sensacionDeseada", e.target.value)
              }
              multiline
              minRows={3}
              readOnly={readOnly}
            />
            <Field
              xs={12}
              md={6}
              label={t("skillFicha.fields.rejects")}
              value={form.IdentidadJson.rechaza}
              onChange={(e) =>
                setSection("IdentidadJson", "rechaza", e.target.value)
              }
              multiline
              minRows={3}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.rejectsHelp")}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.wordsYes")}
              value={form.IdentidadJson.mantrasSi}
              onChange={(e) =>
                setSection("IdentidadJson", "mantrasSi", e.target.value)
              }
              multiline
              minRows={4}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.wordsYesHelp")}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.wordsNo")}
              value={form.IdentidadJson.prohibidos}
              onChange={(e) =>
                setSection("IdentidadJson", "prohibidos", e.target.value)
              }
              multiline
              minRows={4}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.wordsNoHelp")}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.links")}
              value={form.IdentidadJson.links}
              onChange={(e) =>
                setSection("IdentidadJson", "links", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
          </Section>

          <Section section={SECTIONS[2]} readOnly={readOnly} index={2}>
            <Field
              label={t("skillFicha.fields.colors")}
              value={form.UniversoVisualJson.coloresCore}
              onChange={(e) =>
                setSection("UniversoVisualJson", "coloresCore", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.colorsHelp")}
            />
            <Field
              label={t("skillFicha.fields.seasonColors")}
              value={form.UniversoVisualJson.coloresTemporada}
              onChange={(e) =>
                setSection(
                  "UniversoVisualJson",
                  "coloresTemporada",
                  e.target.value,
                )
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
            <Field
              label={t("skillFicha.fields.vetoColors")}
              value={form.UniversoVisualJson.coloresVeto}
              onChange={(e) =>
                setSection("UniversoVisualJson", "coloresVeto", e.target.value)
              }
              multiline
              minRows={2}
              md={12}
              readOnly={readOnly}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.prints")}
              value={form.UniversoVisualJson.prints}
              onChange={(e) =>
                setSection("UniversoVisualJson", "prints", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.printsHelp")}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.materialsYes")}
              value={form.UniversoVisualJson.materialesSi}
              onChange={(e) =>
                setSection("UniversoVisualJson", "materialesSi", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.materialsYesHelp")}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.materialsNo")}
              value={form.UniversoVisualJson.materialesNo}
              onChange={(e) =>
                setSection("UniversoVisualJson", "materialesNo", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.details")}
              value={form.UniversoVisualJson.hardwareDetalles}
              onChange={(e) =>
                setSection(
                  "UniversoVisualJson",
                  "hardwareDetalles",
                  e.target.value,
                )
              }
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.detailsHelp")}
            />
          </Section>

          <Section section={SECTIONS[3]} readOnly={readOnly} index={3}>
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.typologies")}
              value={form.ProductoJson.tipologiasMarcadas}
              onChange={(e) =>
                setSection("ProductoJson", "tipologiasMarcadas", e.target.value)
              }
              placeholder={t("skillFicha.fields.typologiesPh")}
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.typologiesHelp")}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.typologyDetail")}
              value={form.ProductoJson.tipologiasDetalle}
              onChange={(e) =>
                setSection("ProductoJson", "tipologiasDetalle", e.target.value)
              }
              helperText={t("skillFicha.fields.typologyDetailHelp")}
              multiline
              minRows={8}
              readOnly={readOnly}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.brandChecklist")}
              value={form.ProductoJson.checklistMarca}
              onChange={(e) =>
                setSection("ProductoJson", "checklistMarca", e.target.value)
              }
              multiline
              minRows={4}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.brandChecklistHelp")}
            />
          </Section>

          <Section section={SECTIONS[4]} readOnly={readOnly} index={4}>
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.learnings")}
              value={form.MemoriaStageJson.learnings}
              onChange={(e) =>
                setSection("MemoriaStageJson", "learnings", e.target.value)
              }
              multiline
              minRows={3}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.learningsHelp")}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.refs")}
              value={form.MemoriaStageJson.refsClave}
              onChange={(e) =>
                setSection("MemoriaStageJson", "refsClave", e.target.value)
              }
              multiline
              minRows={6}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.refsHelp")}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.production")}
              value={form.MemoriaStageJson.produccion}
              onChange={(e) =>
                setSection("MemoriaStageJson", "produccion", e.target.value)
              }
              multiline
              minRows={3}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.productionHelp")}
            />
          </Section>

          <Section section={SECTIONS[5]} readOnly={readOnly} index={5}>
            <Field
              label={t("skillFicha.fields.deliverables")}
              value={form.AgenteWorkJson.entregables}
              onChange={(e) =>
                setSection("AgenteWorkJson", "entregables", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
            <Field
              label={t("skillFicha.fields.format")}
              value={form.AgenteWorkJson.formatoSalida}
              onChange={(e) =>
                setSection("AgenteWorkJson", "formatoSalida", e.target.value)
              }
              readOnly={readOnly}
            />
          </Section>

          <Section section={SECTIONS[6]} readOnly={readOnly} index={6}>
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.good")}
              value={form.BuenosMalosJson.buenos}
              onChange={(e) =>
                setSection("BuenosMalosJson", "buenos", e.target.value)
              }
              multiline
              minRows={4}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.goodHelp")}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.bad")}
              value={form.BuenosMalosJson.malos}
              onChange={(e) =>
                setSection("BuenosMalosJson", "malos", e.target.value)
              }
              multiline
              minRows={4}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.badHelp")}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.golden")}
              value={form.BuenosMalosJson.fraseOro}
              onChange={(e) =>
                setSection("BuenosMalosJson", "fraseOro", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
              helperText={t("skillFicha.fields.goldenHelp")}
            />
          </Section>

          <Section section={SECTIONS[7]} readOnly={readOnly} index={7}>
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.checklist")}
              value={form.ChecklistJson.items}
              onChange={(e) =>
                setSection("ChecklistJson", "items", e.target.value)
              }
              multiline
              minRows={5}
              readOnly={readOnly}
            />
          </Section>

          <Section section={SECTIONS[8]} readOnly={readOnly} index={8}>
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.comment")}
              value={form.CierreJson.comentario}
              onChange={(e) =>
                setSection("CierreJson", "comentario", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
            <Field
              xs={12}
              md={12}
              label={t("skillFicha.fields.attachments")}
              value={form.CierreJson.adjuntos}
              onChange={(e) =>
                setSection("CierreJson", "adjuntos", e.target.value)
              }
              multiline
              minRows={2}
              readOnly={readOnly}
            />
          </Section>
        </Box>
      </Box>

      {isMobile && (
        <Paper
          elevation={10}
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            p: 1.5,
            zIndex: theme.zIndex.appBar,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.background.paper, 0.96),
            backdropFilter: "blur(8px)",
          }}
        >
          {ActionBar}
        </Paper>
      )}

      <SkillConfirmDialog
        open={Boolean(confirmKind && confirmDialogProps)}
        onClose={() => setConfirmKind(null)}
        confirming={saving}
        {...(confirmDialogProps || {
          title: "",
          description: "",
          onConfirm: () => setConfirmKind(null),
        })}
      />
    </Box>
  );
}
